import React from "react";
import { Link as RouterLink } from "react-router-dom";
import uuid from "uuid";
import styled from "styled-components";
import {
  Button as RebassButton,
  Text as RebassText,
  Box as RebassBox,
  Heading as RebassHeading,
  Flex as RebassFlex,
  Link as RebassLink
} from "rebass";
import {
  fontFamily,
  buttonStyle,
  borders,
  borderColor,
  fontSize,
  height,
  color,
  space,
  width,
  fontWeight,
  display,
  position,
  maxWidth,
  top,
  bottom,
  left,
  right,
  style
} from "styled-system";

import { GoGitBranch } from "react-icons/go";
import { IoLogoTwitter, IoLogoGithub, IoMdHand } from "react-icons/io";
import { FaCookieBite } from "react-icons/fa";
export const Button = styled(RebassButton)(
  fontFamily,
  buttonStyle,
  borderColor
);

export const Span = styled(props => <RebassText as="span" {...props} />)(
  space,
  borders,
  borderColor
);
export const ExternalLink = styled(({ ...props }) => (
  <RebassLink pb="0" target="_blank" {...props} />
))``;
export const Hr = styled.hr(borderColor, space, width);
Hr.defaultProps = {
  width: 1,
  m: 0
};
export const Flex = styled(RebassFlex)`
  ${borders} ${borderColor};
  ${maxWidth} ${height};
`;
export const Box = styled(RebassBox)`
  ${display} ${position};
  ${borders} ${borderColor};
  ${maxWidth} ${height};
`;
export const AbsoluteFlex = styled(Flex)`
${position};
  ${top} ${bottom} ${left} ${right}
`;
export const Code = ({ ...props }) => (
  <RebassText {...props} fontSize="0" fontFamily="code" />
);

const Icons = {
  twitter: IoLogoTwitter,
  github: IoLogoGithub,
  gitBranch: GoGitBranch,
  cookie: FaCookieBite,
  hand: IoMdHand
};
export const Icon = styled(({ name, ...props }) => {
  const Component = Icons[name];
  if (Component) {
    return <Component {...props} />;
  }
  return null;
})`
  height: 100%;
  padding: 6px 0;
  width: auto;
  ${color};
  ${space};
`;

export const Text = styled(({ css, ...props }) => (
  <RebassText
    fontSize="1"
    fontFamily="proxima"
    color="white"
    lineHeight="2"
    width="100%"
    css={{ ...css }}
    {...props}
  />
))`
  ${display};
`;
export const withPosition = comp => {
  return styled(comp)`
  ${position}
  ${top}
  ${bottom}
  ${right}
  ${left}
  `;
};
export const SectionHeading = ({ css, ...props }) => (
  <RebassHeading
    fontSize="1"
    fontFamily="proxima"
    pb={1}
    css={{ textTransform: "capitalize", ...css }}
    {...props}
  />
);
export const DisplayHeading = ({ css, ...props }) => (
  <RebassHeading
    fontSize={["4", "4", "4", "5"]}
    fontFamily="proxima"
    css={{ textTransform: "capitalize", ...css }}
    {...props}
  />
);
export const Link = styled(RouterLink)`
text-decoration: none;
  ${space}
  ${fontSize}
  ${fontFamily}
  ${fontWeight}
  ${color}
  ${borders}
  ${borderColor}
`;
Link.defaultProps = {
  fontFamily: "proxima"
};
export const ExtraInfoHeading = ({ css, ...props }) => (
  <RebassHeading
    {...props}
    fontSize={[2, "4"]}
    fontFamily="proxima"
    color="lines"
    lineHeight="0.7em"
    css={{ ...css, textTransform: "uppercase" }}
  />
);
const ratio = style({
  prop: "ratio",
  cssProperty: "padding-bottom",
  key: "ratios",
  transformValue: n => `${n * 100}%`,
  scale: ["100"]
});
// need styled wrapper to make it responsive
export const RatioBox = styled(({ ratio, ...props }) => <Box {...props} />)`
  position: relative;
  &:before {
    content: "";
    position: relative;
    display: block;
    ${ratio};
  }
`;

RatioBox.defaultProps = {
  width: 1,
  ratio: 100
};
export const RatioImage = ({ src, alt, children, css, ...props }) => (
  <RatioBox
    {...props}
    css={{
      backgroundImage: `url(${src})`,
      backgroundSize: "cover",
      backgroundPosition: "50%",
      ...css
    }}
    role="img"
    ariaLabel={alt}
  >
    {children}
  </RatioBox>
);

export const FullWrapper = ({ css, ...props }) => (
  <Flex {...props} px={[2, 2, 0]} css={{ ...css }} bg="background" />
);

FullWrapper.defaultProps = {
  width: 1,
  justifyContent: "center",
  height: "100%"
};

const ListItem = ({ number, children, css, ...props }) => (
  <Text css={{ textTransform: "capitalize", ...css }} {...props}>
    <Code pr="1" color="passive" fontSize="0" css="display: inline-block;">
      0{number}
    </Code>
    {children}
  </Text>
);
ListItem.defaultProps = {
  pb: 1
};
export const List = ({ items, itemProps, ...props }) => (
  <ol {...props}>
    {items.map((c, i) => (
      <ListItem key={uuid()} number={i} {...itemProps}>
        {c}
      </ListItem>
    ))}
  </ol>
);
